import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  GraduationCap,
  Plus,
  Trash2,
  Eye,
  X,
  ArrowLeft,
  ArrowRight,
  FileCheck,
  UploadCloud,
  FileText,
} from "lucide-react";

export type QualificationRow = {
  institution: string;
  qualification: string;
  year: string;
  cert_url?: string;
  cert_file?: string; // raw stored path, sent back so edits preserve the file
};

// Keep in step with the backend cap (scanFileRules: max 10 MB) so the user is
// told up-front instead of only after a failed upload.
const MAX_FILE_MB = 10;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

type Props = {
  busy: boolean;
  onBack: () => void;
  readOnly?: boolean;
  onSubmit: (payload: {
    qualifications: QualificationRow[];
    certFilesByIndex: Record<number, File | null>;
  }) => Promise<void>;
  initialValues?: {
    qualifications: QualificationRow[];
  } | null;
};

export default function Step2Qualifications({
  onSubmit,
  busy,
  onBack,
  initialValues,
  readOnly,
}: Props) {
  const emptyRow: QualificationRow = useMemo(
    () => ({ institution: "", qualification: "", year: "", cert_url: "" }),
    []
  );

  const [rows, setRows] = useState<QualificationRow[]>([emptyRow]);
  const [files, setFiles] = useState<Record<number, File | null>>({});
  const [previews, setPreviews] = useState<Record<number, string>>({});
  const [fileErrors, setFileErrors] = useState<Record<number, string>>({});

  const hydratedOnceRef = useRef(false);

  useEffect(() => {
    const incoming = initialValues?.qualifications;
    if (!incoming || incoming.length === 0) {
      if (!hydratedOnceRef.current) setRows([emptyRow]);
      return;
    }
    if (hydratedOnceRef.current) return;
    hydratedOnceRef.current = true;

    setRows(
      incoming.map((q) => ({
        institution: q.institution ?? "",
        qualification: q.qualification ?? "",
        year: String(q.year ?? ""),
        cert_url: q.cert_url ?? "",
        cert_file: q.cert_file ?? undefined,
      }))
    );
  }, [initialValues, emptyRow]);

  const setRow = (i: number, k: keyof QualificationRow, v: string) => {
    setRows((r) => r.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  };

  const addRow = () => setRows((s) => [...s, { ...emptyRow }]);

  const removeRow = (i: number) => {
    setRows((s) => s.filter((_, idx) => idx !== i));
    // Re-indexing logic for files and previews
    const shiftMap = (prev: Record<number, any>) => {
      const next: Record<number, any> = {};
      Object.keys(prev).forEach((k) => {
        const idx = Number(k);
        if (idx === i) return;
        next[idx > i ? idx - 1 : idx] = prev[idx];
      });
      return next;
    };
    setFiles(shiftMap);
    setPreviews(shiftMap);
  };

  const clearFileError = (i: number) =>
    setFileErrors((e) => {
      if (!(i in e)) return e;
      const n = { ...e };
      delete n[i];
      return n;
    });

  const handleFile = (i: number, file: File | null) => {
    if (!file) {
      setFiles((s) => ({ ...s, [i]: null }));
      clearFileError(i);
      setPreviews((p) => {
        const n = { ...p };
        delete n[i];
        return n;
      });
      return;
    }

    // Reject an oversized file here (before submit) with a clear message, rather
    // than letting the server bounce it back.
    if (file.size > MAX_FILE_BYTES) {
      setFileErrors((e) => ({
        ...e,
        [i]: `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The maximum is ${MAX_FILE_MB} MB — please upload a smaller or compressed file.`,
      }));
      return; // keep any previously accepted file untouched
    }

    clearFileError(i);
    setFiles((s) => ({ ...s, [i]: file }));
    if (isImageLike(file.type, file.name)) {
      const url = URL.createObjectURL(file);
      setPreviews((p) => ({ ...p, [i]: url }));
    }
  };

  return (
    <form
      className="space-y-8 pb-10"
      onSubmit={async (e) => {
        e.preventDefault();
        if (busy) return;
        // Don't submit while an oversized-file message is showing.
        if (Object.keys(fileErrors).length > 0) return;
        await onSubmit({ qualifications: rows, certFilesByIndex: files });
      }}
    >
      {/* Header */}
      <div className="border-b border-gray-100 pb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-900 rounded-xl">
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Academic Qualifications
            </h2>
            <p className="text-sm text-gray-500">
              List your educational history and upload relevant certificates.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {rows.map((r, i) => (
          <div
            key={i}
            className="group relative bg-white border border-gray-200 rounded-2xl p-6 transition-all hover:border-gray-300 hover:shadow-sm"
          >
            {/* Row Number Badge */}
            <div className="absolute -left-3 top-6 w-8 h-8 bg-gray-900 text-white rounded-lg flex items-center justify-center text-xs font-bold shadow-lg">
              {i + 1}
            </div>

            <div className="grid md:grid-cols-12 gap-6">
              <div className="md:col-span-5">
                <Field
                  label="Institution"
                  value={r.institution}
                  onChange={(v: string) => setRow(i, "institution", v)}
                  placeholder="University of Lagos"
                />
              </div>
              <div className="md:col-span-4">
                <Field
                  label="Qualification"
                  value={r.qualification}
                  onChange={(v: string) => setRow(i, "qualification", v)}
                  placeholder="B.Sc. Computer Science"
                />
              </div>
              <div className="md:col-span-3">
                <Field
                  label="Year Graduated"
                  value={r.year}
                  onChange={(v: string) => setRow(i, "year", v)}
                  placeholder="2018"
                />
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-50 grid md:grid-cols-2 gap-6 items-end">
              {/* File Status & Upload */}
              <div className="space-y-3">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">
                  Certificate Document
                </label>

                <div className="flex flex-wrap gap-3">
                  {/* Existing Badge */}
                  {r.cert_url && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 border border-green-100 rounded-xl text-xs font-medium">
                      <FileCheck className="w-4 h-4" />
                      On File
                      <a
                        href={r.cert_url}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1 hover:bg-green-100 rounded-md transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  )}

                  {/* New File Badge */}
                  {files[i] && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-100 rounded-xl text-xs font-medium">
                      <FileText className="w-4 h-4" />
                      <span className="max-w-[100px] truncate">
                        {files[i]?.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleFile(i, null)}
                        className="p-1 hover:bg-blue-100 rounded-md"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="relative group/upload">
                  <input
                    type="file"
                    accept=".pdf,image/*"
                    className="absolute inset-0 opacity-0 cursor-pointer z-10"
                    onChange={(e) => handleFile(i, e.target.files?.[0] || null)}
                  />
                  <div className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-500 group-hover/upload:border-gray-400 transition-all">
                    <UploadCloud className="w-4 h-4" />
                    {files[i] || r.cert_url
                      ? "Replace Certificate"
                      : "Upload Certificate (PDF/Image)"}
                  </div>
                </div>

                {fileErrors[i] ? (
                  <p className="text-[11px] text-red-600 font-medium">{fileErrors[i]}</p>
                ) : (
                  <p className="text-[10px] text-gray-400">PDF/JPG/PNG · up to {MAX_FILE_MB} MB</p>
                )}
              </div>

              {/* Delete Button */}
              <div className="flex justify-end">
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    className="flex items-center gap-2 px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg text-xs font-bold transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove Entry
                  </button>
                )}
              </div>
            </div>

            {/* Preview Thumbnails */}
            {(previews[i] ||
              (r.cert_url && isImageLike(undefined, r.cert_url))) && (
              <div className="mt-4">
                <ImagePreview
                  src={previews[i] || r.cert_url || ""}
                  alt="Certificate Preview"
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-2 px-4 py-2 text-gray-900 border-2 border-gray-900 rounded-xl text-xs font-bold hover:bg-gray-900 hover:text-white transition-all shadow-sm"
      >
        <Plus className="w-4 h-4" />
        Add Another Qualification
      </button>

      {!readOnly && (
        <div className="flex justify-between items-center pt-8 border-t border-gray-100">
          <button
            type="button"
            disabled={busy}
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 border border-gray-200 rounded-xl font-bold text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <button
            type="submit"
            disabled={busy}
            className="group flex items-center gap-3 px-8 py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg shadow-gray-200"
          >
            {busy ? "Processing..." : "Save & Continue"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      )}
    </form>
  );
}

/* ---------- Sub-components & Helpers ---------- */

function isImageLike(mime?: string, name?: string) {
  if (mime?.startsWith("image/")) return true;
  return /\.(png|jpe?g|webp)$/i.test(name || "");
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">
        {label}
      </label>
      <input
        className="w-full p-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-400 transition-all"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function ImagePreview({ src, alt }: { src: string; alt: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="relative w-32 h-20 rounded-lg overflow-hidden border border-gray-200 group/img">
        <img src={src} alt={alt} className="w-full h-full object-cover" />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 flex items-center justify-center transition-opacity"
        >
          <Eye className="w-5 h-5 text-white" />
        </button>
      </div>

      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-8 backdrop-blur-sm"
        >
          <div className="relative max-w-4xl w-full">
            <button className="absolute -top-12 right-0 text-white flex items-center gap-2 font-bold uppercase text-xs tracking-widest">
              Close <X className="w-5 h-5" />
            </button>
            <img
              src={src}
              alt={alt}
              onClick={(e) => e.stopPropagation()}
              className="w-full h-auto max-h-[80vh] object-contain rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </>
  );
}
